import { ReactNode, createContext, useState, useMemo, useCallback } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { formatDateWzTimezone, getValidDate } from 'src/utils/dateTime';

//
//

type DateCell = Date | null;

type DateRange = { from: DateCell; to: DateCell };

export type DateFilterContextProps = {
  handleChange: (newValue: DateRange) => void;
  dateRange: DateRange;
  // Identifies where the applied range lives: one per programme, plus one for merchant reporting
  scopeKey: string;
  dateFrom?: string;
  dateTo?: string;
  cumulativeDateFrom: string;
  cumulativeDateTo?: string;
  selectedTimezone: TIMEZONE;
  setSelectedTimezone: (timezone: TIMEZONE) => void;
};

export enum TIMEZONE {
  UTC = 'UTC',
  LOCAL = 'LOCAL',
}

const EMPTY_RANGE: DateRange = { from: null, to: null };

const MERCHANT_SCOPE = 'merchant';

const DateFilterContext = createContext<DateFilterContextProps>({
  handleChange: () => {},
  dateRange: EMPTY_RANGE,
  scopeKey: MERCHANT_SCOPE,
  cumulativeDateFrom: '',
  selectedTimezone: TIMEZONE.LOCAL,
  setSelectedTimezone: () => {},
});

type DateFilterProviderProps = {
  children: ReactNode;
};

// Programme tabs share a range per programme code; everything else falls under merchant reporting
const getScopeKey = (pathname: string) => {
  const match = matchPath('/programmes/:type/:code/*', pathname);
  const code = match?.params?.code;

  if (code && match?.params?.type !== 'calculate') {
    return `programme:${code.toUpperCase()}`;
  }

  return MERCHANT_SCOPE;
};

function DateFilterProvider({ children }: DateFilterProviderProps) {
  const { pathname } = useLocation();
  const scopeKey = getScopeKey(pathname);

  // Kept in memory only, so ranges reset on refresh / session close
  const [rangesByScope, setRangesByScope] = useState<Record<string, DateRange>>({});
  const [selectedTimezone, setSelectedTimezone] = useState<TIMEZONE>(TIMEZONE.LOCAL);

  const dateRange = rangesByScope[scopeKey] ?? EMPTY_RANGE;

  const handleChange = useCallback((newValue: DateRange) => {
    if (newValue) {
      setRangesByScope((prev) => ({
        ...prev,
        [scopeKey]: { from: getValidDate(newValue.from), to: getValidDate(newValue.to) },
      }));
    }
  }, [scopeKey]);

  const dateFrom = useMemo(() =>
    dateRange.from ? formatDateWzTimezone(dateRange.from, selectedTimezone, true) : undefined,
  [dateRange?.from, selectedTimezone]);

  const dateTo = useMemo(() =>
    dateRange.to ? formatDateWzTimezone(dateRange.to, selectedTimezone) : undefined,
  [dateRange?.to, selectedTimezone]);

  const cumulativeDateFrom = useMemo(() => {
    return formatDateWzTimezone(new Date('01/01/1970'), selectedTimezone, true);
  }, [selectedTimezone]);

  const cumulativeDateTo = dateFrom;

  return (
    <DateFilterContext.Provider
      value={{
        dateRange,
        handleChange,
        scopeKey,
        dateFrom,
        dateTo,
        cumulativeDateFrom,
        cumulativeDateTo,
        selectedTimezone,
        setSelectedTimezone,
      }}
    >
      {children}
    </DateFilterContext.Provider>
  );
}

export { DateFilterProvider, DateFilterContext };
